/*-
 * #%L
 * Sagae
 * %%
 * Copyright (C) 2020 - 2023 OFB
 * %%
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 * #L%
 */
import { PROFIL_UTILISATEUR } from "@/model/bean/EnumHelper"
import {
  EvaluationDTO,
  NotificationPPResponseDTO,
  ProjetAnnuelForInstructionDTO,
  ProjetInscriptionDTO,
  EtablissementDTO,
  FilterDTO,
  PaginationParameter,
  PeriodDTO,
  ProjetAnnuelDTO,
  ProjetAnnuelLightDTO,
  ProjetPatchDTO,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import { AbstractTypedRestService } from "@/services/rest/AbstractTypedRestService"
import { ProjectLabelsService } from "./ProjetLabelsService"
import { UsersService } from "./../users/UsersService"

type ProjetAnnuelBase = ProjetAnnuelDTO | ProjetAnnuelLightDTO

export class CarnetExportResult {
  blob: Blob
  fileName: string | undefined
}

export class ProjectService extends AbstractTypedRestService<ProjetAnnuelDTO> {
  static INSTANCE = new ProjectService()

  projectLabelsService = ProjectLabelsService.INSTANCE

  async getPeriods(projectId?: string): Promise<PeriodDTO[]> {
    return super.typedGetV2<PeriodDTO>("projets/" + projectId + "/periods", 2)
  }

  async getProjetAnnuel(annualProjectId?: string): Promise<ProjetInscriptionDTO> {
    return await super.typedGetSingleV2<ProjetInscriptionDTO>("projets/" + annualProjectId, 2)
  }

  isProjetReadOnly(
    user: UtilisateurDTO | undefined,
    projet: ProjetAnnuelDTO,
    shouldBeReadOnlyIfInPause: boolean,
  ): boolean {
    // Vérifier si projet.anneeScolaireFin est défini et non nul
    if (projet.anneeScolaireFin) {
      // Check 1: in annee scolaire has ended (and hence there is a newer projet year) => readonly
      // endOfSchoolYear.getDate() +1 pour prendre compte le jour de la fin de l'année scolaire (31 juillet)
      const endOfSchoolYearIncluded = new Date(projet.anneeScolaireFin)
      endOfSchoolYearIncluded?.setDate(endOfSchoolYearIncluded.getDate() + 1)
      if (endOfSchoolYearIncluded?.getTime() <= Date.now()) {
        return true
      }
    }

    // Check 2: if project in pause/archive => readonly
    if ((projet.etat == "EN_PAUSE" && shouldBeReadOnlyIfInPause) || projet.etat == "ARCHIVE") {
      return true
    }

    // Check 3: if user is accompagnateur national or animateur from correct grae => not readonly
    if (!user) {
      return true
    }
    if (
      UsersService.INSTANCE.isUserAccompagnateurFromRegion(user, projet) &&
      user.profil != "ACCOMPAGNATEUR_REGIONAL"
    ) {
      return false
    } else {
      // Check 3 : user is a PP, can edit only if he is part of the project
      let allowedUserIds = projet.enseignants.map((e) => e.id)
      allowedUserIds = allowedUserIds.concat(projet.referents.map((r) => r.id))
      return allowedUserIds.indexOf(user.id) == -1
    }
  }

  /**
   * Détermine si le panneau d'instruction doit être visible pour l'utilisateur connecté.
   * Le panneau est visible pour les super administrateurs et les utilisateurs qui ne sont pas porteurs de projet.
   *
   * @param loggedUser - L'utilisateur connecté
   * @returns true si le panneau d'instruction doit être affiché, false sinon
   */
  canSeeInstructionPanel(loggedUser: UtilisateurDTO | undefined): boolean {
    if (!loggedUser) {
      return false
    }

    // Super administrateur peut toujours voir le panneau d'instruction
    if (loggedUser.profil === PROFIL_UTILISATEUR.SUPER_ADMINISTRATEUR) {
      return true
    }

    // Les autres utilisateurs peuvent voir le panneau s'ils ne sont pas porteurs de projet
    return !UsersService.INSTANCE.isPorteurProjet(loggedUser)
  }

  async getPendingInvitationsToProject(): Promise<ProjetAnnuelDTO[]> {
    return super.typedGetV2<ProjetAnnuelDTO>(`projets/en-attente`, 2)
  }

  async getProjectHistory(annualProjectId?: string): Promise<ProjetAnnuelForInstructionDTO[]> {
    return super.typedGetV2<ProjetAnnuelForInstructionDTO>(
      `projets/` + annualProjectId + `/history`,
      2,
    )
  }

  async getProjetsForPorteurProjet(
    user: UtilisateurDTO,
    filtersList: Array<FilterDTO>,
  ): Promise<ProjetAnnuelDTO[]> {
    let profil = ""
    if (user.profil === PROFIL_UTILISATEUR.ENSEIGNANT) {
      profil = "enseignant"
    } else if (user.profil === PROFIL_UTILISATEUR.REFERENT) {
      profil = "referent"
    }
    const allParameter = { ...new PaginationParameter(), all: true, pageNumber: 1, pageSize: -1 }
    let projects = await super
      .typeGetPaginatedV2<ProjetAnnuelDTO>(
        `/projets/${profil}/${user.id}`,
        2,
        allParameter,
        filtersList,
      )
      .then((result) => (result.elements ? result.elements : []))

    // Ref #8888 hide refused dossiers inscription from list
    projects = projects.filter(
      (p) => p.typeDossier != "DOSSIER_INSCRIPTION" || p.statut != "REFUSE",
    )
    return projects
  }

  async getEtablissementProjects(etablissement: EtablissementDTO): Promise<ProjetAnnuelDTO[]> {
    return super.typedGetV2("/projets/etablissement/" + etablissement.codeEtablissement, 2)
  }

  async getProjectsOfStructure(structureId: string): Promise<ProjetAnnuelLightDTO[]> {
    return super.typedGetV2("/projets/structure/" + structureId, 2)
  }

  async sendAnswersAboutPendingInvitations(
    projects: ProjetAnnuelDTO[],
    user: UtilisateurDTO,
  ): Promise<void> {
    await Promise.all(
      projects.map(async (project) => {
        if (user.id && project.consentementValide.includes(user.id)) {
          await super.typedGetV2(
            "/projets/" + project.projetId + "/accept_project_inclusion/" + user.id,
            2,
          )
        } else if (user.id && !project.consentementEnAttente.includes(user.id)) {
          await super.typedGetV2(
            "/projets/" + project.projetId + "/decline_project_inclusion/" + user.id,
            2,
          )
        }
      }),
    )
  }

  async sendAnswerAboutPendingInvitation(project: ProjetAnnuelDTO, userId: string): Promise<void> {
    if (project.consentementValide.includes(userId)) {
      await this.acceptProjetInclusion(project.projetId, userId)
    } else if (!project.consentementEnAttente.includes(userId)) {
      await this.declineProjetInclusion(project.projetId, userId)
    }
  }

  async acceptProjetInclusion(projectId: string, userId: string): Promise<void> {
    await super.typedGetV2("/projets/" + projectId + "/accept_project_inclusion/" + userId, 2)
  }

  async declineProjetInclusion(projectId: string, userId: string): Promise<void> {
    await super.typedGetV2("/projets/" + projectId + "/decline_project_inclusion/" + userId, 2)
  }

  async getNotificationPP(idGrae: string | undefined): Promise<NotificationPPResponseDTO[]> {
    let urlNotif = "/projets/notification-pp"
    if (idGrae) {
      urlNotif += "?idGrae=" + idGrae
    }
    return super.typedGetV2(urlNotif, 2)
  }

  async resetNotificationPP(idGrae: string | undefined): Promise<void> {
    let url = "/reset-notification-pp"
    if (idGrae) {
      url += "?idGrae=" + idGrae
    }
    await super.typedGetV2("/projets" + url, 2)
  }

  async createProject(project: ProjetAnnuelDTO): Promise<ProjetAnnuelDTO> {
    return super.typedPostV2("/projets/", 2, project)
  }

  async createProjectDemande(project: ProjetAnnuelDTO): Promise<ProjetAnnuelDTO> {
    return super.typedPostV2("/projets/demande", 2, project)
  }

  async patchProject(projectId: string, project: ProjetPatchDTO): Promise<ProjetPatchDTO> {
    return super.typedPatchV2("/projets/" + projectId, 2, project)
  }

  async deleteProject(projectId: string): Promise<void> {
    await super.typedDeleteV2("/projets/" + projectId, 2)
    return Promise.resolve()
  }

  async saveProject(project: ProjetAnnuelDTO): Promise<ProjetAnnuelDTO> {
    if (project.typeDossier == "DOSSIER_INSCRIPTION") {
      return await super.typedPutV2("projets/inscription/" + project.id, 2, project)
    } else {
      return await super.typedPutV2("projets/" + project.id, 2, project)
    }
  }

  async submitProjetInscription(project: ProjetInscriptionDTO): Promise<ProjetInscriptionDTO> {
    return super.typedPutV2("projets/inscription/" + project.id + "/submit", 2, project)
  }

  async submitProjetLabellisation(project: ProjetAnnuelDTO): Promise<ProjetAnnuelDTO> {
    return super.typedPutV2("projets/labellisations/" + project.id + "/submit", 2, project)
  }

  async unlockProject(projectId: string): Promise<ProjetAnnuelDTO> {
    return super.typedPutV2("projets/unlock/" + projectId, 2, new ProjetAnnuelDTO())
  }

  async unarchiveProject(projectId: string): Promise<ProjetAnnuelDTO> {
    return super.typedPutV2("projets/unarchive/" + projectId, 2, new ProjetAnnuelDTO())
  }

  async updateInstructeur(projetId: string, instructeur: UtilisateurDTO): Promise<UtilisateurDTO> {
    return super.typedPutV2("/projets/inscription/" + projetId + "/instructeur", 2, instructeur)
  }

  async removeInstructeur(
    projetId: string,
    instructeur: UtilisateurDTO,
  ): Promise<UtilisateurDTO[]> {
    const instructeurId = instructeur.id
    return super.typedDeleteV2(
      "/projets/inscription/" + projetId + "/instructeur/" + instructeurId,
      2,
    )
  }

  // VALID
  async updateEvaluation(
    projetAnnuel: ProjetAnnuelDTO,
    evaluation: EvaluationDTO,
  ): Promise<EvaluationDTO> {
    return super.typedPutV2(
      "projets/labellisation/" + projetAnnuel.id + "/evaluation/" + evaluation.id,
      2,
      evaluation,
    )
  }

  // VALID
  async updateQualifications(projetAnnuel: ProjetAnnuelDTO): Promise<ProjetAnnuelDTO> {
    return super.typedPutV2(
      "projets/labellisation/qualifications/" + projetAnnuel.id,
      2,
      projetAnnuel,
    )
  }

  async checkRightsAccessToProject(projectAnnuelId: string): Promise<boolean> {
    return super.typedGetV2<boolean>(`projets/${projectAnnuelId}/check-access-rights`, 2)
  }

  getProjectStatusLabel(projetAnnuel: ProjetAnnuelBase, loggedUser: UtilisateurDTO): string {
    return this.projectLabelsService.getProjectStatusLabel(projetAnnuel, loggedUser)
  }

  getProjectConsultActionLabel(projetAnnuel: ProjetAnnuelBase, loggedUser: UtilisateurDTO): string {
    return this.projectLabelsService.getProjectConsultActionLabel(projetAnnuel, loggedUser)
  }

  getProjectLightConsultActionLabel(
    projetAnnuelLight: ProjetAnnuelLightDTO,
    loggedUser: UtilisateurDTO,
  ): string {
    return this.projectLabelsService.getProjectConsultActionLabel(projetAnnuelLight, loggedUser)
  }

  getLabelForLastLabellisation(projetAnnuel: ProjetAnnuelDTO): string {
    return this.projectLabelsService.getLabelForLastLabellisation(projetAnnuel)
  }

  /**
   * Mapper pour convertir ProjetAnnuelDTO vers ProjetAnnuelLightDTO
   * Utilisé pour optimiser les appels API qui ne nécessitent que les données essentielles
   */
  projetAnnuelToLight(projetAnnuel: ProjetAnnuelDTO): ProjetAnnuelLightDTO {
    return {
      id: projetAnnuel.id!,
      projetId: projetAnnuel.projetId!,
      annee: projetAnnuel.annee!,
      nom: projetAnnuel.nom!,
      grae: projetAnnuel.grae,
      etat: projetAnnuel.etat!,
      statut: projetAnnuel.statut!,
      typeDossier: projetAnnuel.typeDossier,
      lastEvaluationTypeDossier: projetAnnuel.lastEvaluationTypeDossier,
      natureAireEducative: projetAnnuel.natureAireEducative,
      etablissements: projetAnnuel.etablissements,
    } as ProjetAnnuelLightDTO
  }

  async downloadCarnetDeBord(
    projectId: string | undefined,
    mapData: string,
  ): Promise<CarnetExportResult> {
    const apiUrl = this.getUrl(`projets/${projectId}/export/carnet-de-bord`, 2)

    const requestBody = {
      fileFormat: "odt",
      mapScreenshotData: mapData,
    }

    // Effectuer la requête POST
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody), // Convertir l'objet en JSON
    })

    // Vérifier si la requête a réussi
    if (!response.ok) {
      throw new Error(
        `Erreur lors de l'export du carnet de bord: HTTP-error status [${response.status}]`,
      )
    }

    const blob = await response.blob()
    const fileName = response.headers.get("Content-Disposition")?.match(/filename="(.+)"/)?.[1]

    return {
      blob: blob,
      fileName: fileName,
    }
  }
}
